import Immutable from 'immutable';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import BulkAddSampleGroupsFlow from 'bulk-import/BulkAddSampleGroupsFlow';
import {REQUESTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Grid, SelectGridMode} from 'constants/SampleConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import EditButtons, {DEFAULT_EDIT_BUTTON_STYLES, EditButtonStates} from 'metadata/EditButtons';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import ManagedColumnsSimpleTableEditable, {
  TableWithoutColumnWidths,
} from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {
  ColumnConstraint,
  MultiSelectionBox,
  TableColumnType,
  TableData,
  TableHeaderControlsArgs,
} from 'notebook/tables/TableConstants';
import {getBooleanCellValue} from 'notebook/tables/typed-values/DataTypes';
import notifications from 'notifications';
import {RequestFullfillableStatuses} from 'requests/RequestConstants';
import {UIRequestTask} from 'requests/tasks/RequestTaskConstants';
import sampleGroupsToContainerIds from 'samples/sampleGroupsToContainerIds';
import * as StorableActions from 'samples/StorableActions';
import SelectParentGridModal from 'samples/storables/SelectParentGridModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import * as net from 'util/Net';
import unexpectedCallback from 'util/unexpectedCallback';
import {ImportedRow, makeSampleGroupFromImportedRow} from 'workflows/samples/makeSampleGroup';
import {PreparedSampleGroup, PreparedUISampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';
import * as SampleGroupDAO from 'workflows/samples/SampleGroupDAO';

import {ContainerGraphQL} from '../../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {RequestTaskGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTaskGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {SampleGroupSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/SampleGroupSchemaGraphQL';
import RequestSampleTableAddMenu from './RequestSampleTableAddMenu';
import {
  convertToTable,
  getColumnConstraints,
  getColumnWidths,
  getRequestTaskKey,
  getSampleGroupKey,
} from './RequestSampleTableUtil';
import {TABLE_NAME} from './SamplesTableConstants';

export interface EditingRequestSampleTableProps {
  canToggleEditMode: boolean;
  isWritable: boolean;
  isSaving: boolean;
  requestId: string | null; // If not specified - we are creating a new request
  requestTaskSchemasById: {[taskSchemaId: string]: RequestTaskSchemaGraphQL};
  requestTasks: Array<RequestTaskGraphQL | UIRequestTask>;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  sampleGroupSchema: SampleGroupSchemaGraphQL;
  // We are populating cid of SampleGroups in DraftableRequestSampleTable, so that we could
  // identify them similarly to UISampleGroups. This is also the reason the callbacks take sampleGroupCid
  sampleGroups: Array<PreparedUISampleGroup>;

  containersById: {[containerId: string]: ContainerGraphQL | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  fulfillableStatuses: RequestFullfillableStatuses;

  onBulkAddRows: (sampleGroups: Array<PreparedSampleGroup | PreparedUISampleGroup>) => void;
  onBulkRemoveRows: (selectedRowIds: Array<string>) => void;
  onUpdateSampleGroupIdsByRequestTaskId: (sampleGroupIdsByRequestTaskId: {
    [taskId: string]: Array<string>;
  }) => void;

  onCancelEdit: () => void;
  onSave: () => void;
}

export default class EditingRequestSampleTable extends React.Component<EditingRequestSampleTableProps> {
  _shouldDisableToggleTask(selectedRowIds: Array<string>, selectedColumns: Array<ColumnConstraint>): boolean {
    if (!selectedRowIds.length) {
      return true;
    }
    for (let i = 0; i < selectedColumns.length; i++) {
      const columnDefinition = selectedColumns[i].columnDefinition;
      if (columnDefinition && columnDefinition.type === TableColumnType.REQUEST_TASK_SELECTION) {
        return false;
      }
    }
    return true;
  }

  _handleClickToggleTask(sampleGroupIds: Array<string>, selectedColumns: Array<ColumnConstraint>): void {
    const requestTasks: Array<RequestTaskGraphQL | UIRequestTask> = [];
    selectedColumns.forEach(({columnDefinition}) => {
      if (columnDefinition && columnDefinition.type === TableColumnType.REQUEST_TASK_SELECTION) {
        requestTasks.push(columnDefinition.requestTask);
      }
    });
    const requestTaskIds = requestTasks.map((task) => getRequestTaskKey(task));
    const updates: {[requestTaskId: string]: Array<string>} = {};
    const allTasksSelected = requestTaskIds.every((requestTaskId) => {
      const sampleGroupIdsWithTask = this.props.sampleGroupIdsByRequestTaskId[requestTaskId];
      return sampleGroupIds.every((idOrCid) => sampleGroupIdsWithTask.includes(idOrCid));
    });

    requestTaskIds.forEach((requestTaskId) => {
      const sampleGroupIdsWithTask = this.props.sampleGroupIdsByRequestTaskId[requestTaskId];
      updates[requestTaskId] = allTasksSelected
        ? _.difference(sampleGroupIdsWithTask, sampleGroupIds)
        : _.union(sampleGroupIdsWithTask, sampleGroupIds);
    });

    this.props.onUpdateSampleGroupIdsByRequestTaskId({
      ...this.props.sampleGroupIdsByRequestTaskId,
      ...updates,
    });
  }

  _getToggleTaskButton(selectionBox: MultiSelectionBox | null): JSX.Element | null {
    if (
      this.props.requestTaskSchemasById == null ||
      this.props.requestTasks == null ||
      this.props.requestTasks.length === 0
    ) {
      return null;
    }
    const table = this._getSpreadsheetTable();
    const selectedRowIds = selectionBox
      ? table
          .rowData!.slice(selectionBox.minI, selectionBox.maxI + 1)
          .map((rowData) => rowData.sampleGroupId!)
      : [];
    const selectedColumns = selectionBox
      ? table.columnConstraints.slice(selectionBox.minJ, selectionBox.maxJ + 1)
      : [];
    return (
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MINI}
        text="Toggle"
        icon="toggle"
        disabled={this._shouldDisableToggleTask(selectedRowIds, selectedColumns as Array<ColumnConstraint>)}
        onClick={() => {
          this._handleClickToggleTask(selectedRowIds, selectedColumns as Array<ColumnConstraint>);
        }}
      />
    );
  }

  _getEditButtons(): JSX.Element {
    return (
      <div className="u-display-inline-block">
        <EditButtons
          buttonStyles={{
            ...DEFAULT_EDIT_BUTTON_STYLES,
            editButton: ButtonStyle.SECONDARY,
            doneButton: ButtonStyle.SECONDARY,
            savingButton: ButtonStyle.SECONDARY,
          }}
          editingState={this.props.isSaving ? EditButtonStates.SAVING : EditButtonStates.EDITING}
          onCancel={this.props.onCancelEdit}
          onComplete={this.props.onSave}
          onEdit={unexpectedCallback}
        />
      </div>
    );
  }

  _getRightHeader({isCollapsed, multiSelectionBox}: TableHeaderControlsArgs): JSX.Element | null {
    if (!this.props.isWritable || isCollapsed) {
      return null;
    }

    const showSeparator = this._getToggleTaskButton(multiSelectionBox) != null;

    return (
      <div className="u-vertical-center-items">
        {!this.props.isSaving && <div>{this._getToggleTaskButton(multiSelectionBox)}</div>}
        {!this.props.isSaving && this.props.canToggleEditMode && showSeparator && (
          <div className="requestSampleTable-headerButtons-separator" />
        )}

        {this.props.canToggleEditMode && this._getEditButtons()}
      </div>
    );
  }

  _addImportedRows = async (rows: Array<ImportedRow>): Promise<void> => {
    const sampleGroups = rows.map((row: ImportedRow) =>
      makeSampleGroupFromImportedRow(graphQLToREST('SampleGroupSchema', this.props.sampleGroupSchema), row)
    );

    const containerIds = sampleGroupsToContainerIds(sampleGroups);
    await Promise.all([
      StorableActions.bulkFetchSummaries(containerIds),
      StorableSummaryExtrasManager.fetchMulti(containerIds),
    ]);

    this.props.onBulkAddRows(sampleGroups);
  };

  _importFromSpreadsheet = (): void => {
    BulkAddSampleGroupsFlow.open({
      sampleGroupSchemaId: this.props.sampleGroupSchema.id,
      onImportRows: (rows: Array<ImportedRow>) => {
        this._addImportedRows(rows);
        analytics.deprecatedTrackEvent(REQUESTS_ANALYTICS_ACTIONS.IMPORTED_SAMPLE_GROUPS);
      },
    });
  };

  _importFromGrid = async (): Promise<void> => {
    const result = (await runModal(SelectParentGridModal, {
      mode: SelectGridMode.EXISTING,
      submitStyle: ButtonStyle.SECONDARY,
      submitText: 'Import',
    })) as {storable: Grid};
    if (!result) {
      return;
    }
    const grid = result.storable;
    if (!grid) {
      return;
    }
    LoadingScreen.show(`Importing from ${typeToName(APIUtil.apiIdToType(grid.id))}...`);
    try {
      const {importedRows} = await SampleGroupDAO.getNewSampleGroupsFromGrid({
        gridId: grid.id,
        sampleGroupSchemaId: this.props.sampleGroupSchema.id,
      });
      await this._addImportedRows(importedRows);
      notifications.success(
        `Imported ${importedRows.length} ${pluralize(importedRows.length, 'row')} from ${grid.name}`
      );
    } catch (err) {
      if (net.isServerRequestError(err)) {
        notifications.stickyNotifyForFriendlyError(err, 'get-samples-from-grid-failed');
        return;
      }
      throw err;
    } finally {
      LoadingScreen.hide();
    }
  };

  _handleRowsRemoved(table: TableData): void {
    // Remove any removed rows
    const originalRowIds = Immutable.Set(
      this.props.sampleGroups.map((sampleGroup) => getSampleGroupKey(sampleGroup))
    );
    const removedRowIds = originalRowIds.subtract(table.rowData!.map((rowData) => rowData.sampleGroupId!));
    for (let i = 0; i < removedRowIds.count(); i++) {
      analytics.deprecatedTrackEvent(REQUESTS_ANALYTICS_ACTIONS.DELETED_SAMPLE_GROUP);
    }
    if (removedRowIds.count()) {
      this.props.onBulkRemoveRows(removedRowIds.toArray());
    }
  }

  _handleRowsEdited(table: TableData): void {
    // get new sampleGroupIdsByRequestTaskId
    let shouldPerformUpdate = false;
    const updates: {[requestTaskId: string]: Array<string>} = {};
    for (let i = 0; i < table.columnConstraints.length; i++) {
      const columnDefinition = table.columnConstraints[i]!.columnDefinition;
      if (columnDefinition && columnDefinition.type === TableColumnType.REQUEST_TASK_SELECTION) {
        const requestTaskId = getRequestTaskKey(columnDefinition.requestTask);
        const originalSampleGroups = Immutable.Set(this.props.sampleGroupIdsByRequestTaskId[requestTaskId]);
        const newSampleGroups = [];
        for (let j = 0; j < table.table.length; j++) {
          const sampleGroupId = table.rowData![j].sampleGroupId!;
          const newValue = getBooleanCellValue(table.table[j][i]);
          if (newValue !== originalSampleGroups.has(sampleGroupId)) {
            shouldPerformUpdate = true;
          }
          if (newValue) {
            newSampleGroups.push(sampleGroupId);
          }
        }
        updates[requestTaskId] = newSampleGroups;
      }
    }
    if (shouldPerformUpdate) {
      this.props.onUpdateSampleGroupIdsByRequestTaskId({
        ...this.props.sampleGroupIdsByRequestTaskId,
        ...updates,
      });
    }
  }

  _handleUpdate = (index: number, {table, selection}: {table: TableData; selection: Selection}): void => {
    this._handleRowsRemoved(table);
    this._handleRowsEdited(table);
  };

  _renderAddButton(args: TableHeaderControlsArgs): JSX.Element | null {
    if (args.isCollapsed) {
      return null;
    }
    return (
      <RequestSampleTableAddMenu
        key="add-menu"
        onAddSampleGroup={(sampleGroup) => {
          this.props.onBulkAddRows([sampleGroup]);
        }}
        onImportFromGrid={this._importFromGrid}
        onImportFromSpreadsheet={this._importFromSpreadsheet}
        sampleGroupSchema={this.props.sampleGroupSchema}
        shouldShowImportFromGrid={this.props.sampleGroupSchema.fieldsJson.length === 1}
      />
    );
  }

  _getSpreadsheetTable(): TableWithoutColumnWidths {
    const {
      sampleGroups,
      sampleGroupSchema,
      requestTasks,
      sampleGroupIdsByRequestTaskId,
      requestTaskSchemasById,
      containersById,
      ancestralStorablesById,
    } = this.props;
    return convertToTable({
      sampleGroups,
      sampleGroupSchema,
      requestTasks,
      sampleGroupIdsByRequestTaskId,
      containersById,
      ancestralStorablesById,
      requestTaskSchemasById,
      isReadOnly: false,
      fulfillableStatuses: {}, // Not required when isReadOnly is false
    });
  }

  render(): JSX.Element {
    const {
      sampleGroups,
      sampleGroupSchema,
      requestTasks,
      requestTaskSchemasById,
      containersById,
      ancestralStorablesById,
    } = this.props;
    return (
      <div className="mediocre react-tab-container--mediocre-tableWrapper">
        <ManagedColumnsSimpleTableEditable
          initialColumnWidths={getColumnWidths({
            columnConstraints: getColumnConstraints({
              sampleGroupSchema,
              requestTasks,
              sampleGroups,
              containersById,
              requestTaskSchemasById,
              ancestralStorablesById,
              isReadOnly: false,
            }),
          })}
          tableWithoutColumnWidths={this._getSpreadsheetTable()}
          {...standaloneProps}
          tableLabel={TABLE_NAME}
          cid={`request-submission-table-${this.props.requestId || 'new'}`}
          locked={false}
          viewer={false}
          hideFormulaBox={true}
          enableFillDown={true}
          hasPlaceholderRows={sampleGroups.length === 0}
          headerControls={(args) => this._getRightHeader(args)}
          leftHeaderControls={(args) => this._renderAddButton(args)}
          // storeState is invoked when selection changes
          storeState={() => {}}
          restoreState={() => {}}
          isLeftAlignColumnHeader={true}
          updateHandler={this._handleUpdate}
        />
      </div>
    );
  }
}
